import { zodResolver } from "@hookform/resolvers/zod";
import translateServerErrorIntoFriendlyMessage from "components/discussions/form/translateServerErrorIntoFriendlyMessage";
import Editor from "components/editor/Editor";
import type { ReplyPostData } from "components/forum/form";
import { replyPostInputFormDataShape } from "components/forum/form";
import usePostActions from "components/forum/hooks/usePostActions";
import InlineErrorSpan from "components/shared/form/InlineErrorSpan";
import { Button } from "components/ui/Button";
import { Form, FormMessage } from "components/ui/Form";
import classNames from "components/ui/classNames";
import PostRepliesQuery from "lib/graphql/queries/engagement/PostRepliesQuery";
import LinkButton from "components/ui/LinkButton";
import React from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

interface IForumEditPostProps {
  onCancel: () => void;
  shortId: string;
  objectId: string;
  initialState: string;
  isReply?: boolean;
}

const ForumEditPost = React.forwardRef<HTMLDivElement | null, IForumEditPostProps>(
  ({ onCancel, shortId, objectId, initialState, isReply }, ref) => {
    const { editPost, editPostReply } = usePostActions();
    const intl = useIntl();

    const form = useForm<ReplyPostData>({
      mode: "onSubmit",
      resolver: zodResolver(replyPostInputFormDataShape(intl)),
      defaultValues: {
        bodyAsJson: "",
        bodyAsPlainText: "",
      },
    });

    const {
      formState: { touchedFields, errors, isSubmitting },
    } = form;

    const handleSubmit = async ({ bodyAsJson, bodyAsPlainText }: ReplyPostData) => {
      try {
        if (isReply) {
          await editPostReply({
            variables: {
              objectId,
              bodyAsJson,
              bodyAsPlainText,
            },
            refetchQueries: [
              {
                query: PostRepliesQuery,
                variables: {
                  postShortId: shortId,
                  cursor: null,
                },
              },
            ],
          });
        } else {
          await editPost({
            variables: {
              title: "",
              objectId,
              bodyAsJson,
              bodyAsPlainText,
            },
            refetchQueries: [
              {
                query: PostRepliesQuery,
                variables: {
                  postShortId: shortId,
                  cursor: null,
                },
              },
            ],
          });
        }

        setTimeout(() => {
          form.reset();
          onCancel();
        }, 1);
      } catch (err) {
        form.setError("root.serverError", {
          message: translateServerErrorIntoFriendlyMessage(
            err,
            intl,
            intl.formatMessage({
              defaultMessage: "Não foi possível enviar sua mensagem agora. Tente novamente mais tarde.",
              id: "AsTfN9",
            }),
          ),
        });
      }
    };

    return (
      <div ref={ref} className="col-span-4 bg-white dark:bg-slate-800 md:col-span-9">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-y-2">
            <Editor
              className="h-fit w-full rounded-xl bg-white dark:bg-slate-900"
              onChange={(bodyAsJson, bodyAsPlainText) => {
                form.setValue("bodyAsJson", bodyAsJson);
                form.setValue("bodyAsPlainText", bodyAsPlainText);
              }}
              onSubmit={() => {
                form.handleSubmit(handleSubmit)();
              }}
              initialState={initialState}
            />

            <div>
              {touchedFields.bodyAsJson && errors.bodyAsJson && (
                <InlineErrorSpan>{errors.bodyAsJson.message || ""}</InlineErrorSpan>
              )}
            </div>

            <FormMessage asRootError={true} />

            <div className="flex w-full justify-end gap-x-4">
              <LinkButton
                disabled={isSubmitting}
                variant="danger"
                onClick={() => {
                  form.reset();
                  onCancel();
                }}
              >
                <FormattedMessage defaultMessage="Cancelar" id="nZLeaQ" />
              </LinkButton>

              <Button
                className={classNames(
                  "flex h-9 w-auto select-none items-center justify-center overflow-visible rounded border-none",
                  "bg-brand px-3 text-sm text-white shadow-md hover:bg-brand/50 focus:outline-none",
                  {
                    "cursor-default bg-brand/50": isSubmitting,
                  },
                )}
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <FormattedMessage defaultMessage="Aguarde..." id="VZazm7" />
                ) : (
                  <FormattedMessage defaultMessage="Editar" id="VGHwB5" />
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    );
  },
);

export default ForumEditPost;
